import MultiSelect from '@/Components/MultiSelect/MultiSelect';
import PureTextButton from '@/Components/PureTextButton/PureTextButton';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import TextBlockOverviewStore from '@/Stores/TextBlockOverviewStore';
import loadingCircle from '@assets/Icons/loading-circle.svg';
import icnArrowLeftWhite from '@legacyImg/icnArrowLeftWhite.png';
import icnArrowRightWhite from '@legacyImg/icnArrowRightWhite.png';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as API from '../../API';
import Button from '../../Components/Button/Button';
import type TextBlockResult from '../../Models/TextBlock/TextBlockResult';
import Styles from './TextBlockOverview.module.scss';
import TableHeaderSortDirection from '@/Components/TableHeaderSortDirection/TableHeaderSortDirection';
import { DobInput } from '@dedalusdiit/dob-components';

const exportAllTextblocksToG2 = (): void => {
    void API.textblocks.exportAllTextblocksToG2();
};

interface TextBlockOverviewProps {
    initialState: FilingCabinetItem;
}

const TextBlockOverview = observer(({ initialState }: TextBlockOverviewProps): React.ReactElement => {
    const { t } = useTranslation();
    const fcContext = useFilingCabinetContext();
    const navigate = useNavigate();

    const store = useMemo(() => new TextBlockOverviewStore(new FilingCabinetStore(initialState, fcContext)), [initialState]);

    const {
        pagination, sorting,
        procedures, modalities, radiologists, languages,
        chosenProcedures, chosenModalities, chosenRadiologists, chosenLanguages,
    } = store.state;

    const columnHelper = useMemo(() => createColumnHelper<TextBlockResult>(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('Name', {
            header: t('Name'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('SearchTerm', {
            header: t('SearchTerm'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('Pronunciation', {
            id: 'Pronunciation',
            header: t('Pronunciation'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('UseInReporting', {
            id: 'UseInReporting',
            header: t('Reporting'),
            cell: (info: any) => {
                const value = info.getValue();
                return (
                    <>
                        {value === true ? '✅' : '❌'}
                    </>
                );
            },
            size: 20,
        }),
        columnHelper.accessor('UseInG2Speech', {
            id: 'UseInG2Speech',
            header: 'G2Speech',
            cell: (info: any) => {
                const value = info.getValue();
                return (
                    <>
                        {value === true ? '✅' : '❌'}
                    </>
                );
            },
            size: 20,
        }),
        columnHelper.accessor('AutoInsert', {
            id: 'AutoInsert',
            header: 'AutoInsert',
            cell: (info: any) => {
                const value = info.getValue();
                return (
                    <>
                        {value === true ? '✅' : '❌'}
                    </>
                );
            },
            size: 20,
        }),
        columnHelper.accessor('Language', {
            id: 'Language',
            header: t('Language'),
            cell: (info) => info.getValue(),
            size: 20,
        }),
        columnHelper.accessor('ForRadiologists', {
            id: 'ForRadiologists',
            header: t('UseForRadiologists'),
            cell: (info: any) => {
                const value = info.getValue();
                return Array.isArray(value) ? value.join(', ') : value;
            },
        }),
        columnHelper.accessor('Procedures', {
            id: 'Procedures',
            header: `${t('Procedures')}`,
            cell: (info) => info.getValue().map((x) => x.Value).join(', '),
        }),
        columnHelper.accessor('Modalities', {
            id: 'Modalities',
            header: `${t('Modalities')}`,
            cell: (info) => info.getValue().map((x) => x.Value).join(', '),
        }),
        columnHelper.accessor('ModifiedBy', {
            id: 'ModifiedBy',
            header: `${t('LastModifiedBy')}`,
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('LastModification', {
            id: 'LastModification',
            header: t('LastModification'),
            cell: (info) => {
                const value = info.getValue();
                if (!value) return '';

                const date = new Date(value);
                return moment(date).format('DD/MM/YYYY');
            },
            size: 40,
        }),
    ], []);

    const table = useReactTable({
        columns,
        data: store.filteredTextBlocks,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: (updater) => {
            const newVal = updater instanceof Function ? updater(pagination) : updater;
            store.setPagination(newVal);
        },
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: (updater) => {
            const newVal = updater instanceof Function ? updater(sorting) : updater;
            store.setSorting(newVal);
        },
        state: {
            sorting,
            pagination,
        },
    });

    return (
        <div className={Styles.textBlockOverview}>
            <div className={Styles.dataContainer}>
                <span className={Styles.title}>{t('TextBlocks')}</span>
                <div className={Styles.tableOverviewFilters}>
                    <DobInput
                        value={store.state.globalFilter}
                        onValueChange={(value: string) => store.set('globalFilter', value)}
                        placeholder={t('Search')}
                    />
                    <MultiSelect
                        onChange={store.setChosenProcedures}
                        title={t('Procedures')}
                        options={procedures}
                        selected={chosenProcedures}
                        useContains
                    />
                    <MultiSelect
                        onChange={store.setChosenModalities}
                        title={t('Modalities')}
                        options={modalities}
                        selected={chosenModalities}
                        useContains
                    />
                    <MultiSelect
                        onChange={store.setChosenRadiologists}
                        title={t('Radiologists')}
                        options={radiologists}
                        selected={chosenRadiologists}
                        useContains
                    />
                    <MultiSelect
                        onChange={store.setChosenLanguages}
                        title={t('Languages')}
                        options={languages}
                        selected={chosenLanguages}
                        useContains
                    />
                    <input type="checkbox" id="onlyAutoInsert" onChange={store.setShowOnlyAutoInsert} checked={store.state.showOnlyAutoInsert} />
                    <label htmlFor="onlyAutoInsert">{t('ShowOnlyAutoInsert')}</label>
                    <Button mediumGreen onClick={store.simulateSearch}>{t('Search')}</Button>
                </div>
                <div className={Styles.tableContainer}>
                    <table>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id} className={Styles.headerRow}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} className={Styles.header} {...{ onClick: header.column.getToggleSortingHandler() }}>
                                            <div>
                                                {flexRender(
                                                    t(header.column.columnDef.header as string),
                                                    header.getContext(),
                                                )}
                                                <TableHeaderSortDirection sortDirection={header.column.getIsSorted()} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {store.loading && <Loading />}
                            {!store.loading && table.getRowModel().rows.length === 0 && <NoResults />}
                            {!store.loading && table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    onClick={() => navigate('/System/DetailTextBlock/' + row.original.ID)}
                                    className={Styles.resultRow}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            style={{
                                                width: cell.column.getSize(),
                                            }}
                                            key={cell.id}
                                            className={Styles.rowCell}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={Styles.tableFooter}>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 25, 50, 100].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize}
                            </option>
                        ))}
                    </select>
                    <div className={Styles.footerActions}>
                        <Button silent onDark small onClick={() => table.previousPage()}>
                            <img
                                src={icnArrowLeftWhite}
                                alt="previous"
                                width={20}
                            />
                        </Button>
                        <div>{table.getState().pagination.pageIndex + 1} / {table.getPageCount().toLocaleString()}</div>
                        <Button silent onDark small onClick={() => table.getCanNextPage() ? table.nextPage() : null}>
                            <img src={icnArrowRightWhite} alt="next" width={20} />
                        </Button>
                        <div>|</div>
                        <div>{t('Total')}: {table.getRowCount().toLocaleString()}</div>
                        <div>
                            <PureTextButton textContent={t('Reload')} onClick={store.reload} />
                        </div>
                    </div>
                    <div className={Styles.footerRightButtons}>
                        <Button silent onDark onClick={() => exportAllTextblocksToG2()}>{t('Export')}</Button>
                        <Button silent onDark onClick={() => navigate('/System/AddTextBlock')}>{t('AddNew')}</Button>
                    </div>
                </div>
            </div>
        </div>
    );
});

function NoResults(): React.ReactElement {
    const { t } = useTranslation();

    return (
        <tr className={Styles.noResult}>
            <td colSpan={50}>{t('NoResultsFound')}</td>
        </tr>
    );
}

function Loading(): React.ReactElement {
    return (
        <tr className={Styles.noResult}>
            <td colSpan={50}>
                <img src={loadingCircle} alt="radial progress" />
            </td>
        </tr>
    );
}

export default TextBlockOverview;
